import accountingPeriodFormScreenshot from '@assets/screenshots/accounting_period_form.png';
import chartOfAccountsScreenshot from '@assets/screenshots/create_chart_of_accounts.png';
import journalDraftScreenshot from '@assets/screenshots/create_a_new_jouenel_draft.png';
import postingRuleStepOneScreenshot from '@assets/screenshots/create_new_posting_rule_1.png';
import postingRuleStepTwoScreenshot from '@assets/screenshots/create_new_posting_rule_2.png';
import taxRateScreenshot from '@assets/screenshots/create_new_tax_rate.png';
import taxRefundScreenshot from '@assets/screenshots/create_new_tax_refund.png';
import paymentMappingScreenshot from '@assets/screenshots/payment_mapping_page.png';

export type AccountingGuideLevel = 'beginner' | 'intermediate' | 'advanced';

export interface AccountingGuideFieldExample {
  field: string;
  meaning: string;
  example: string;
}

export interface AccountingGuideWalkthroughImage {
  src: string;
  alt: string;
  caption?: string;
}

export interface AccountingGuideWalkthrough {
  id: string;
  page: string;
  title: string;
  summary: string;
  images: AccountingGuideWalkthroughImage[];
  fields: AccountingGuideFieldExample[];
  tips?: string[];
}

export interface AccountingGuideDocument {
  level: AccountingGuideLevel;
  label: string;
  title: string;
  summary: string;
  audience: string;
  markdown: string;
  walkthroughs: AccountingGuideWalkthrough[];
}

const SHARED_ACCOUNTING_WALKTHROUGHS: AccountingGuideWalkthrough[] = [
  {
    id: 'chart-of-accounts-form',
    page: 'Chart of Accounts',
    title: 'How to create a new account',
    summary:
      'Use this form when you want to add a money bucket such as Cash, Revenue, Expense, or Tax.',
    images: [
      {
        src: chartOfAccountsScreenshot,
        alt: 'Create chart of accounts form',
        caption:
          'Create a clear account that other journals and reports can rely on.',
      },
    ],
    fields: [
      {
        field: 'Account code',
        meaning: 'The short number that identifies the account in reports.',
        example: '4100 for Food Sales or 1000 for Cash on Hand',
      },
      {
        field: 'Account name',
        meaning: 'The human-friendly name of the account.',
        example: 'Food Sales',
      },
      {
        field: 'Account type',
        meaning: 'The major family the account belongs to.',
        example: 'Revenue for sales accounts, Asset for cash accounts',
      },
      {
        field: 'Normal balance',
        meaning:
          'Tells the system whether the account usually grows on the debit or credit side.',
        example: 'Credit for revenue, Debit for cash',
      },
      {
        field: 'Parent account',
        meaning:
          'Lets you group smaller accounts under a bigger reporting group.',
        example: 'Food Sales under POS Revenue',
      },
    ],
    tips: [
      'Use names your team can recognize quickly.',
      'Only create a new account when an existing one cannot represent the activity clearly.',
    ],
  },
  {
    id: 'journal-entry-form',
    page: 'Journal Entries',
    title: 'How to create a journal draft',
    summary:
      'Use a journal draft when you need to record or correct something manually before posting it.',
    images: [
      {
        src: journalDraftScreenshot,
        alt: 'Create a new journal draft form',
        caption:
          'Draft first, review the balances, then post only when the entry is correct.',
      },
    ],
    fields: [
      {
        field: 'Entry date',
        meaning: 'The business date the journal belongs to.',
        example:
          'The day the rent expense happened or the correction was identified',
      },
      {
        field: 'Source module',
        meaning: 'Explains where the event comes from.',
        example: 'Manual when you are entering the journal yourself',
      },
      {
        field: 'Reference type',
        meaning: 'The kind of business record this journal points back to.',
        example: 'Adjustment, booking, settlement, or custom reference',
      },
      {
        field: 'Reference ID',
        meaning:
          'The exact record number or identifier connected to the journal.',
        example: 'INV-1023 or BOOK-204',
      },
      {
        field: 'Memo',
        meaning: 'A short explanation of what this journal is about.',
        example: 'Correcting room revenue for booking BOOK-204',
      },
    ],
    tips: [
      'Each journal must balance: total debits must equal total credits.',
      'Use a memo that helps another accountant understand the entry without asking you.',
    ],
  },
  {
    id: 'tax-rate-form',
    page: 'Tax Rates',
    title: 'How to create a tax rate',
    summary:
      'Use this page to tell the system how tax should be split from sales or purchases.',
    images: [
      {
        src: taxRateScreenshot,
        alt: 'Create a new tax rate form',
        caption:
          'A correct tax setup keeps revenue and tax liability separated in reports.',
      },
    ],
    fields: [
      {
        field: 'Tax name',
        meaning: 'The label your team will recognize in sales and accounting.',
        example: 'VAT 18%',
      },
      {
        field: 'Rate',
        meaning: 'The percentage used by the frontend and accounting logic.',
        example: '18 means 18%',
      },
      {
        field: 'Application',
        meaning:
          'Defines whether the tax is used on sales, purchases, or both.',
        example: 'Sales for VAT collected from customers',
      },
      {
        field: 'Output tax account',
        meaning: 'The liability account for tax collected from customers.',
        example: 'VAT Payable',
      },
      {
        field: 'Input tax account',
        meaning: 'The recoverable account for tax paid on purchases.',
        example: 'Input VAT / Tax Recoverable',
      },
    ],
    tips: [
      'If you choose the wrong tax account, revenue and liabilities can both look wrong.',
    ],
  },
  {
    id: 'accounting-period-form',
    page: 'Accounting Periods',
    title: 'How to open an accounting period',
    summary:
      'Use accounting periods to control which dates are allowed to accept posted journals.',
    images: [
      {
        src: accountingPeriodFormScreenshot,
        alt: 'Accounting period form',
        caption:
          'Open the period before work starts, and close it only after review is complete.',
      },
    ],
    fields: [
      {
        field: 'Period name',
        meaning: 'The label the team will use when reviewing activity.',
        example: 'May 2026',
      },
      {
        field: 'Start date',
        meaning: 'The first date included in the period.',
        example: '2026-05-01',
      },
      {
        field: 'End date',
        meaning: 'The last date included in the period.',
        example: '2026-05-31',
      },
      {
        field: 'Status',
        meaning: 'Shows whether journals can still post into this period.',
        example: 'Open while work is ongoing, Closed after review',
      },
      {
        field: 'Outlet',
        meaning: 'Limits the period to one outlet when needed.',
        example: 'Choose an outlet only when you want outlet-specific control',
      },
    ],
    tips: ['Do not create overlapping periods for the same scope.'],
  },
  {
    id: 'payment-mapping-form',
    page: 'Payment Mappings',
    title: 'How to map a payment method',
    summary:
      'Use this page so the system knows where to debit cash, bank, mobile money, or receivables during postings.',
    images: [
      {
        src: paymentMappingScreenshot,
        alt: 'Payment mappings page',
        caption:
          'Every payment method should point to the right account before live posting begins.',
      },
    ],
    fields: [
      {
        field: 'Payment method',
        meaning:
          'The operational payment option used by staff at settlement time.',
        example: 'Cash, Card, Mobile Money, Credit',
      },
      {
        field: 'Mapped account',
        meaning: 'The accounting account that should receive the debit.',
        example:
          'Cash on Hand for Cash, Mobile Money Clearing for Mobile Money',
      },
      {
        field: 'Payment type',
        meaning: 'Helps the system understand the behavior of the method.',
        example: 'Credit means receivable logic, NC means non-chargeable logic',
      },
    ],
    tips: [
      'Do not leave payment methods unmapped if they are already being used in sales or bookings.',
    ],
  },
  {
    id: 'posting-rule-form',
    page: 'Posting Rules',
    title: 'How to create a posting rule',
    summary:
      'Use posting rules to tell the system which account to debit or credit when a business event happens.',
    images: [
      {
        src: postingRuleStepOneScreenshot,
        alt: 'Create posting rule step one',
        caption: 'First choose the event you want to automate.',
      },
      {
        src: postingRuleStepTwoScreenshot,
        alt: 'Create posting rule step two',
        caption: 'Then choose the direction and target account.',
      },
    ],
    fields: [
      {
        field: 'Source module',
        meaning: 'The part of the app that creates the event.',
        example: 'POS or Accommodation',
      },
      {
        field: 'Event type',
        meaning: 'The business action that should trigger posting.',
        example: 'Sale completed, booking payment received, refund processed',
      },
      {
        field: 'Reference type',
        meaning: 'The record type connected to the rule.',
        example: 'Settlement, booking, payout, tax refund',
      },
      {
        field: 'Direction',
        meaning: 'Says whether the rule is the debit side or credit side.',
        example: 'Debit Cash on Hand or Credit Room Revenue',
      },
      {
        field: 'Account',
        meaning: 'The exact ledger account the rule should post to.',
        example: 'Room Revenue, VAT Payable, Accounts Receivable',
      },
    ],
    tips: [
      'A business event usually needs more than one rule so both debit and credit sides are covered.',
      'Use the guided event and reference selectors to stay consistent.',
    ],
  },
  {
    id: 'tax-refund-form',
    page: 'Tax Refunds',
    title: 'How to record a tax refund setup',
    summary:
      'Use this area when you need rules for returning tax to a customer or adjusting tax after a reversal or cancellation.',
    images: [
      {
        src: taxRefundScreenshot,
        alt: 'Create new tax refund form',
        caption:
          'Tax refunds should be explicit so the system does not leave the tax side behind.',
      },
    ],
    fields: [
      {
        field: 'Refund scenario',
        meaning:
          'The business case that explains why the tax is being adjusted.',
        example: 'Cancelled paid booking or refunded sales order',
      },
      {
        field: 'Tax rate',
        meaning: 'The tax definition being reversed or adjusted.',
        example: 'VAT 18%',
      },
      {
        field: 'Refund account direction',
        meaning:
          'Shows which account gets debited or credited during the adjustment.',
        example: 'Debit VAT Payable when reducing previously recognized tax',
      },
      {
        field: 'Notes or description',
        meaning: 'Gives other accountants context for the refund logic.',
        example: 'Tax refund rule for same-day booking cancellations',
      },
    ],
    tips: [
      'Refund logic should match the original revenue and tax posting pattern.',
    ],
  },
];

export const ACCOUNTING_GUIDE_DOCUMENTS: AccountingGuideDocument[] = [
  {
    level: 'beginner',
    label: 'Beginner',
    title: 'Accounting for First-Time Users',
    audience: 'New accountants and first-time finance users',
    summary:
      'Learn what the accounting pages mean, what journals and tax records do, and what to set up first.',
    markdown: `
# Accounting for First-Time Users

## What this page is for

This guide explains the accounting feature in simple language.

It helps answer:

- What is this page doing?
- What is a journal?
- Why does tax need its own record?
- What should I set up first?

## What accounting means in this system

Think of accounting as the money story of the business.

The system watches what happens in:

- Sales
- Accommodation
- Booking.com flows

Then it writes the financial meaning of those actions.

## What an account is

An account is a labeled bucket for money or value.

Examples:

- Cash
- Bank
- Mobile Money
- Sales
- Room Revenue
- Tax Payable
- Expenses

## What a journal is

A journal is a written money story.

It explains:

- what happened
- which accounts changed
- how much moved

Simple example:

\`\`\`text
Customer pays cash for food

Dr Cash
Cr Sales
\`\`\`

That means:

- cash went up
- sales went up

## Why journals matter

Journals are important because they:

- keep history clear
- help catch mistakes
- support reports
- show proof of what happened

## What a tax record is

A tax record helps separate:

- money that belongs to the business
- money that belongs to the government as tax

If tax is mixed into sales carelessly, reports become wrong.

## What posting means

Posting means saving a journal officially.

Before posting, it is a draft.

After posting, it becomes part of the true accounting history.

## What reversal means

A reversal is a safe undo.

Instead of deleting the past, the system creates a new journal that cancels the wrong one.

## The main accounting pages

## Accounting Dashboard

Use this page to quickly see:

- revenue
- profit
- cash
- alerts
- recent activity

## Chart of Accounts

Use this page to review and organize the money buckets.

## Journal Entries

Use this page to inspect detailed financial stories and create manual entries when needed.

## Reports

Use this page to review financial results in a structured way.

## Settings

Use this page to prepare the accounting rules:

- tax rates
- payment mappings
- posting rules
- accounting periods

## What to set up first

1. Review Chart of Accounts
2. Review Tax Rates
3. Review Payment Mappings
4. Review Posting Rules
5. Open an Accounting Period
6. Start reviewing journals

## The most important things to remember

1. Accounts are money buckets.
2. Journals are money stories.
3. Tax should be recorded separately.
4. Posted journals should not be edited carelessly.
5. Reversals are safer than deleting history.
6. Reports are only as good as the journals underneath them.
`,
    walkthroughs: SHARED_ACCOUNTING_WALKTHROUGHS,
  },
  {
    level: 'intermediate',
    label: 'Intermediate',
    title: 'Accounting for Everyday Finance Users',
    audience: 'Accountants with some accounting knowledge',
    summary:
      'Understand how smartPOS maps operational events into journals, reports, tax, and receivable/deferred-revenue logic.',
    markdown: `
# Accounting for Everyday Finance Users

## Purpose

This guide is for someone who already understands:

- journals
- debits and credits
- tax
- receivables
- deferred revenue

## How smartPOS uses accounting

Operational modules create events.

The Accounting module translates those events into journals and balances.

Important event sources:

- POS settlements
- accommodation booking payments
- room revenue recognition
- Booking.com payouts and commission flows

## Core accounting structure

The accounting feature is built around:

- Chart of Accounts
- Journal Entries
- Journal Entry Lines
- Accounting Periods
- Tax Rates
- Payment Method Mappings
- Financial Posting Rules

## Chart of Accounts

The chart is company-scoped and should be reviewed before live use.

It already includes seeded account groups for:

- cash and bank
- mobile money
- receivables
- tax
- deferred revenue
- POS revenue
- accommodation revenue
- OTA clearing and commission

## Journal Entries

Key states:

- draft
- posted
- voided
- reversed

Best practice:

- review drafts before posting
- reverse posted errors instead of editing them directly

## Accounting Periods

Periods control when journals are allowed to post.

If posting fails unexpectedly, check whether the relevant period is open.

## Tax Rates

Tax setup should be reviewed for:

- rate
- application
- output account
- input account

If tax is wrong, revenue and liabilities may both be wrong at once.

## Payment Method Mappings

Mappings determine where the debit side of payment goes.

Examples:

- Cash -> Cash account
- Card -> Bank or clearing
- Mobile Money -> Mobile Money account
- Credit -> Receivables

Important interpretation:

- OPEN and PENDING are operational states
- CREDIT is receivables logic
- NC is non-chargeable and not normal collection

## Posting Rules

Posting rules are the automation brain of the module.

They define:

- source module
- event type
- reference type
- direction
- account
- optional metadata

## How the system thinks about main flows

## POS accounting

POS accounting uses settlement as the main financial trigger.

That means the financial story begins at settlement, not just at order creation.

## Accommodation accounting

Booking payments do not always mean earned revenue immediately.

Payments may land in:

- advance guest deposits
- unearned revenue
- receivable settlement

Revenue is then recognized when earned.

## Booking.com accounting

OTA flows can involve:

- receivables or clearing
- commission expense
- payout receipt
- reconciliation differences

## Reports

## Trial Balance

Use this for structural account-balance review.

## General Ledger

Use this for transaction-level investigation.

## Account Activity

Use this for summarized movement analysis by account and date.

## Recommended working order

1. Review accounts
2. Review tax setup
3. Review payment mappings
4. Review posting rules
5. Open periods
6. Review journals
7. Review reports

## Daily routine

1. Check the dashboard
2. Review action-needed items
3. Review failed or pending postings
4. Review key journals
5. Review reports when needed
`,
    walkthroughs: SHARED_ACCOUNTING_WALKTHROUGHS,
  },
  {
    level: 'advanced',
    label: 'Advanced',
    title: 'Accounting for Controllers, Auditors, and Senior Finance Users',
    audience: 'Controllers, auditors, finance leads, and senior accountants',
    summary:
      'Review financial controls, traceability, idempotency, reconciliation surfaces, and implementation boundaries across modules.',
    markdown: `
# Accounting for Controllers, Auditors, and Senior Finance Users

## Purpose

This guide explains how the smartPOS accounting layer behaves as a control system across Sales, Accommodation, and OTA activity.

## System accounting philosophy

The platform treats accounting as the financial interpretation layer of the business, not just as a report output.

Operational modules emit business events.

Accounting:

- classifies them
- maps them
- posts balanced journals
- preserves traceability

## Core control principles

1. Posted journals must remain balanced.
2. Posted journals should not be edited directly.
3. Source-to-journal traceability must be preserved.
4. Duplicate posting must be controlled.
5. Earned revenue timing must remain distinct from cash timing.
6. Tax must not be collapsed into revenue.

## Data model

Key structures:

- accounts
- journal_entries
- journal_entry_lines
- accounting_periods
- financial_event_logs
- payment_method_account_mappings
- tax_rates
- financial_posting_rules

Reporting views:

- trial_balance_view
- general_ledger_view
- account_activity_view

## Chart strategy

The chart uses these primary types:

- asset
- liability
- equity
- revenue
- cogs
- expense
- other_income
- other_expense

Subtypes are descriptive classifiers, not the primary control boundary.

## Journal discipline

The lifecycle supports:

- draft
- posted
- voided
- reversed

Correction should happen through reversal or replacement logic, not silent mutation.

## Financial event logs and idempotency

The financial event log acts as:

- an audit trace
- a duplicate-prevention mechanism
- an exception-management surface

Statuses include:

- pending
- processed
- failed
- ignored

This is the first place to inspect when a posting question arises.

## Payment mapping control

Payment method mapping is company-owned because operational payment methods are shared while financial treatment is company-specific.

Important interpretations:

- OPEN is operational
- PENDING is operational
- CREDIT routes to receivables
- NC is non-chargeable and not standard collection

## Posting rules as automation control

Posting rules are the configurable automation layer.

Advanced users should watch for:

- overlapping rules
- inconsistent event naming
- redundant logic
- metadata drift

## POS model

POS accounting is settlement-driven.

This is intentional and keeps financial recognition tied to actual settlement events.

## Accommodation model

Accommodation accounting separates:

- cash receipt
- deferred revenue
- earned revenue
- receivable creation

Revenue recognition is stay-aware rather than only booking-total aware.

## Booking.com model

The OTA model preserves:

- gross economics
- commission
- clearing / collectible balances
- payout receipt

This avoids collapsing OTA activity into net revenue only.

## Reporting use

## Trial Balance

Use for structural review and month-end reasonableness checks.

## General Ledger

Use for source-linked transaction tracing, reversals, and anomaly review.

## Account Activity

Use for summarized movement investigation between ledger detail and high-level reporting.

## Recommended review routine

### Daily

- review failed and pending financial events
- review unmapped payment methods
- inspect recent journal summaries

### Weekly

- review receivables
- review deferred revenue
- review OTA payout reconciliation

### Month-end

- confirm period discipline
- review trial balance
- inspect reversals
- review exception handling

## Current implementation boundaries

At this stage, the accounting foundation is strong across:

- chart, journals, and periods
- POS settlement accounting
- accommodation payment accounting
- accommodation revenue recognition
- Booking.com accounting foundation

Inventory-driven accounting remains the next major expansion area.
`,
    walkthroughs: SHARED_ACCOUNTING_WALKTHROUGHS,
  },
];
